import React from 'react';
import {
  formatConsensusMultiplierLabel,
  consensusMultiplierFromPickStats,
  isVoteBoosted,
  VOTE_BOOST_ADD
} from 'arena-shared/consensusMultiplier';
import { SACRIFICE_MULTIPLIER_FACTOR } from '../utils/augments';
import { formatBettingPct, hasPickStats, livePickCounts } from '../utils/bettingStats';
import { renderHelpTextWithIcons } from './helpIconTokens';
import './GameStatsRecap.css';

function roundMultiplier(value) {
  return Math.round(Number(value) * 10) / 10;
}

/** This pick's points multiplier (consensus + vote + Multiplier augment). */
export function resolvePickPointsMultiplier({
  pickStats,
  homeScore,
  awayScore,
  playerVotedForMatch = false,
  hasMultiplierAugment = false
}) {
  if (!hasPickStats(pickStats)) return null;
  if (homeScore == null || awayScore == null) return null;
  const h = Number(homeScore);
  const a = Number(awayScore);
  if (!Number.isFinite(h) || !Number.isFinite(a)) return null;

  const voteBoosted = isVoteBoosted(pickStats);
  const voteAdd = voteBoosted && playerVotedForMatch ? VOTE_BOOST_ADD : 0;
  const base = consensusMultiplierFromPickStats(pickStats, homeScore, awayScore, false);
  const multiplierFactor = hasMultiplierAugment ? SACRIFICE_MULTIPLIER_FACTOR : 0;
  // ×1.7 applies to rarity only; community vote +0.1 stays outside the product.
  const total = roundMultiplier(
    multiplierFactor ? base * multiplierFactor + voteAdd : base + voteAdd
  );
  return {
    base,
    voteAdd,
    multiplierFactor,
    total,
    hasStack: voteAdd > 0 || multiplierFactor > 0,
    voteBoosted,
    playerVotedForMatch: Boolean(playerVotedForMatch),
    voteApplied: voteAdd > 0
  };
}

function formatMultiplierValue(resolved) {
  if (!resolved) return null;
  const { base, voteAdd, multiplierFactor, total, hasStack } = resolved;
  if (!hasStack) return formatConsensusMultiplierLabel(total);

  // Keep each "number + icon" as one nowrap unit so icons never orphan onto the next line.
  if (multiplierFactor) {
    const terms = [
      `${base} {{consensus}}`,
      `× ${multiplierFactor} {{m}}`,
      voteAdd ? `+ ${voteAdd} {{vote}}` : null
    ].filter(Boolean);
    return (
      <>
        {formatConsensusMultiplierLabel(total)}
        {' = '}
        {terms.map((term, index) => (
          <span key={`mult-term-${index}`} className="game-stats-recap-mult-term">
            {index > 0 ? ' ' : null}
            {renderHelpTextWithIcons(term)}
          </span>
        ))}
      </>
    );
  }

  const baseTerms = [
    `${base} {{consensus}}`,
    voteAdd ? `+ ${voteAdd} {{vote}}` : null
  ].filter(Boolean);

  return (
    <>
      {formatConsensusMultiplierLabel(total)}
      {' = '}
      {baseTerms.map((term, index) => (
        <span key={`mult-term-${index}`} className="game-stats-recap-mult-term">
          {index > 0 ? ' ' : null}
          {renderHelpTextWithIcons(term)}
        </span>
      ))}
    </>
  );
}

/**
 * Community pick stats + this pick's explained points multiplier.
 */
function GameStatsRecap({
  pickStats,
  homeScore,
  awayScore,
  playerVotedForMatch = false,
  hasMultiplierAugment = false
}) {
  if (!hasPickStats(pickStats)) return null;

  const { outcomeHits, resultHits, totalBets } = livePickCounts(
    pickStats,
    homeScore,
    awayScore
  );
  const resolved = resolvePickPointsMultiplier({
    pickStats,
    homeScore,
    awayScore,
    playerVotedForMatch,
    hasMultiplierAugment
  });
  const consensusOnly = consensusMultiplierFromPickStats(pickStats, homeScore, awayScore, false);
  const multiplierDisplay =
    formatMultiplierValue(resolved) ?? formatConsensusMultiplierLabel(consensusOnly);

  return (
    <div className="game-stats-recap" aria-label="Community pick stats">
      <div className="game-stats-recap-grid">
        <div className="game-stats-recap-stat">
          <span className="game-stats-recap-label">Outcome hit</span>
          <strong>{formatBettingPct(outcomeHits, totalBets)}</strong>
        </div>
        <div className="game-stats-recap-stat">
          <span className="game-stats-recap-label">Exact score</span>
          <strong>{formatBettingPct(resultHits, totalBets)}</strong>
        </div>
        <div className="game-stats-recap-stat game-stats-recap-stat--multiplier">
          <span className="game-stats-recap-label">Points multiplier</span>
          <strong className="game-stats-recap-multiplier-value">{multiplierDisplay}</strong>
        </div>
      </div>
    </div>
  );
}

export default GameStatsRecap;
